// Code that loads on demand, and can be asked for early (on a tap or hover, while idle, at boot) so
// it is usually there by the time it renders.
import { lazy, useState, type ComponentType } from 'react';

/** A dynamic import that runs once, however often it's asked for. A failed load is forgotten, so
 *  the next call tries again (a flaky connection, a deploy in between). */
export function preloadable<T>(load: () => Promise<T>): () => Promise<T> {
  let p: Promise<T> | null = null;
  return () =>
    (p ??= load().catch((err: unknown) => {
      p = null;
      throw err;
    }));
}

/**
 * React.lazy for a component inside a screen (a dialog, a sheet's body). Once its code has loaded
 * (after `preload()`, or an earlier render), it renders straight away instead of suspending for a
 * frame. The choice is made once per mount: switching from the lazy wrapper to the loaded component
 * on a later render would remount it and lose its state (a half-typed form).
 */
export function lazyComponent<P extends object>(load: () => Promise<ComponentType<P>>) {
  let loaded: ComponentType<P> | undefined;
  const preload = preloadable(() =>
    load().then((c) => {
      loaded = c;
      return c;
    }),
  );
  const Lazy = lazy(() => preload().then((c) => ({ default: c })));
  function Component(props: P) {
    const [Direct] = useState(() => loaded);
    return Direct ? <Direct {...props} /> : <Lazy {...props} />;
  }
  return Object.assign(Component, { preload });
}

/** Runs `fn` once the page has loaded and the main thread is idle: for fetching code ahead of
 *  time without competing with what the screen needs now. Returns a cancel function. */
export function whenIdle(fn: () => void, delay = 1000): () => void {
  let cancelled = false;
  let timer = 0;
  let idle = 0;
  const run = () => {
    if (cancelled) return;
    const ric = window.requestIdleCallback;
    if (ric) idle = ric(() => !cancelled && fn(), { timeout: 4000 });
    else timer = window.setTimeout(() => !cancelled && fn(), 200);
  };
  const start = () => (timer = window.setTimeout(run, delay));
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
  return () => {
    cancelled = true;
    window.clearTimeout(timer);
    window.removeEventListener('load', start);
    if (idle) window.cancelIdleCallback?.(idle);
  };
}
